import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { dashboardApi } from '../../lib/api/dashboard'
import type { DashboardReport } from '../../types/dashboard'
import { DashboardHeader, toInputDate, toUrlDate } from './components/DashboardHeader'
import { OverviewSection } from './components/OverviewSection'
import { PerformanceSection } from './components/PerformanceSection'
import { ContentTypeSection } from './components/ContentTypeSection'
import { AdSizeSection } from './components/AdSizeSection'
import { DomainsSection } from './components/DomainsSection'
import { CustomMetricsSection } from './components/CustomMetricsSection'
import { PdfDownloadModal } from './components/PdfDownloadModal'

export function AdUnitDashboardPage() {
  const { campaignId, adUnitId } = useParams<{ campaignId: string; adUnitId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const [report, setReport] = useState<DashboardReport | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showPdfModal, setShowPdfModal] = useState(false)

  const overviewRef = useRef<HTMLDivElement>(null)
  const performanceRef = useRef<HTMLDivElement>(null)
  const contentTypeRef = useRef<HTMLDivElement>(null)
  const adSizeRef = useRef<HTMLDivElement>(null)

  const rawFrom = searchParams.get('date_from') || ''
  const rawTo = searchParams.get('date_to') || ''
  const dateFrom = rawFrom ? toInputDate(rawFrom) : ''
  const dateTo = rawTo ? toInputDate(rawTo) : ''

  useEffect(() => {
    if (!campaignId || !adUnitId) return
    setIsLoading(true)
    setError(null)
    dashboardApi.getAdUnitReport(parseInt(campaignId, 10), parseInt(adUnitId, 10), {
      date_from: rawFrom || undefined,
      date_to: rawTo || undefined,
    })
      .then(res => setReport(res))
      .catch(err => setError(err?.message || 'Failed to load report'))
      .finally(() => setIsLoading(false))
  }, [campaignId, adUnitId, rawFrom, rawTo])

  const handleDateChange = (from: string, to: string) => {
    const params: Record<string, string> = {}
    if (from) params.date_from = toUrlDate(from)
    if (to) params.date_to = toUrlDate(to)
    setSearchParams(params, { replace: true })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading dashboard…</p>
        </div>
      </div>
    )
  }

  if (error || !report) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error || 'Report not found'}</p>
          <button onClick={() => navigate(-1)} className="text-primary-600 hover:underline text-sm">← Go back</button>
        </div>
      </div>
    )
  }

  const resolvedFrom = dateFrom || report.date_range.from
  const resolvedTo = dateTo || report.date_range.to

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <DashboardHeader
        campaignName={report.campaign?.name || `Campaign ${campaignId}`}
        adUnitName={report.ad_unit?.name || `Ad Unit ${adUnitId}`}
        dateFrom={resolvedFrom}
        dateTo={resolvedTo}
        period={report.period}
        onDateChange={handleDateChange}
        onDownloadPdf={() => setShowPdfModal(true)}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <OverviewSection overview={report.overview} sectionRef={overviewRef} />
        <PerformanceSection rows={report.performance} mode="ad_unit" sectionRef={performanceRef} />
        <ContentTypeSection rows={report.content_types} sectionRef={contentTypeRef} />
        <AdSizeSection rows={report.ad_sizes} sectionRef={adSizeRef} />
        <DomainsSection rows={report.top_domains} />
        <CustomMetricsSection entries={report.custom_metrics} mode="ad_unit" />
      </main>

      <PdfDownloadModal
        isOpen={showPdfModal}
        onClose={() => setShowPdfModal(false)}
        report={report}
        mode="ad_unit"
        filename={`dashboard_adunit_${adUnitId}`}
      />
    </div>
  )
}
